import { type ChangeEvent, type MouseEvent, useCallback, useMemo, useState } from 'react';

import isPlainObject from 'lodash.isplainobject';
import { useTranslation } from 'react-i18next';
import isEqualWith from 'lodash.isequalwith';
import cn from 'classnames';

import {
  AdvancedSearch,
  type AdvancedSearchFilterType,
  type DateRangeValueType,
  formatDateRangeView,
  type IEachItem,
  Input,
  Table,
} from '@wf/components';
import { localStorageService } from '@wf/sw-ui-kit';

import { RimmSubEntitySelect } from '../../../../rimm/components/RimmSubEntitySelect';
import { RimmEntitySelect } from '../../../../rimm/components/RimmEntitySelect';
import DepartmentOfficeSelect from './fields/DepartmentOfficeSelect';
import LicenseTypeSelect from './fields/LicenseTypeSelect';
import OgaStatusSelect from './fields/OgaStatusSelect';
import DateRangeField from './fields/DateRangeField';
import StatusSelect from './fields/StatusSelect';

import useLicenseAbbreviation from '../../../../../hooks/useLicenseAbbreviation';
import { Operators } from '../../../../../@types/serviceTypes';
import { PAGE_CONTENT_ID } from '../../../../../constants';
import { type IRestriction } from '../../../../../types';

import {
  type ICommodityRimmEntity,
  type ICompanyRimmEntity,
  type IMinistryDepartmentEntity,
  type IMinistryDepartmentOffice,
  type IMinistryRimmEntity,
} from '../../../../rimm/data/rimmEntityTypes';
import { RimmEntityType, RimmSubEntityType } from '../../../../rimm/data/rimmEntityEnums';
import { getShowFiltersFromLocalStorage } from '../helpers';
import { getLicenseTableColumns } from '../tableConfigs';
import { SearchSelectField } from '../../../configs';

import styles from './LicensesSearch.module.scss';

interface ILicensesSearch {
  columnSettings: IEachItem[];
  saveColumnSettings: (settings: IEachItem[]) => void;
  restrictions: Record<string, IRestriction>;
  setRestrictions: (restrictions: Record<string, IRestriction>) => void;
  isNewLicenses: boolean;
  tableName: string;
}

function LicensesSearch({
  columnSettings,
  saveColumnSettings,
  restrictions,
  setRestrictions,
  isNewLicenses,
  tableName,
}: ILicensesSearch) {
  const { t } = useTranslation();

  const licenseAbbreviation = useLicenseAbbreviation();
  const columns = useMemo(
    () =>
      getLicenseTableColumns(
        tableName,
        t('Type of {{licenseAbbreviation}}', { licenseAbbreviation: t(licenseAbbreviation) })
      ),
    [licenseAbbreviation, t, tableName]
  );

  const [inputValues, setInputValues] = useState<Record<string, string>>(() => ({
    [SearchSelectField.userReferenceNumber]:
      (restrictions[SearchSelectField.userReferenceNumber]?.value as string) ?? '',
    [SearchSelectField.approvalReference]: (restrictions[SearchSelectField.approvalReference]?.value as string) ?? '',
  }));

  const [departmentOffices, setDepartmentOffices] = useState<IMinistryDepartmentOffice[] | null>(null);

  const filters = useMemo(() => {
    const { [SearchSelectField.status]: status, requestNumber, ...filtersRestrictions } = restrictions;

    return Object.values(filtersRestrictions).map((item) => {
      let value = item.value;

      if (typeof value === 'object' && 'operator' in (value as DateRangeValueType)) {
        value = formatDateRangeView(value as DateRangeValueType, 'dd/MM/yyyy', t);
      } else if (isPlainObject(value)) {
        value = (value as SelectOption).value;
      }

      return {
        type: item.label ?? item.field,
        value,
        id: item.field,
      } as AdvancedSearchFilterType;
    });
  }, [restrictions, t]);

  const showFiltersKey = `${tableName}::SHOW_FILTERS`;

  const [showAdvancedFilter, setShowAdvancedFilter] = useState(
    () => getShowFiltersFromLocalStorage(showFiltersKey) ?? !!filters.length
  );

  const toggleFilterSection = () => {
    setShowAdvancedFilter(!showAdvancedFilter);
    localStorageService.set(showFiltersKey, !showAdvancedFilter);
  };

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setInputValues((prev) => ({ ...prev, [name]: value.toUpperCase() }));
  };

  const inputRef = useCallback(
    (ref: HTMLInputElement) => {
      if (ref) {
        const { name } = ref;
        setInputValues((prev) => ({ ...prev, [name]: (restrictions[name]?.value as string)?.toUpperCase() ?? '' }));
      }
    },
    [restrictions]
  );

  const handleDateChange = (dates: DateRangeValueType, field: SearchSelectField, label: string, close: () => void) => {
    handleAddRestriction(
      {
        field,
        label,
        value: dates,
        operator: dates.operator,
      },
      close
    );
  };

  const handleRemoveRestriction = (removedFilter: AdvancedSearchFilterType | null) => {
    if (!removedFilter) {
      const newRestrictions: Record<string, IRestriction> = {};
      const { [SearchSelectField.status]: status, requestNumber } = restrictions;

      if (status) {
        newRestrictions[SearchSelectField.status] = status;
      }

      if (requestNumber) {
        newRestrictions.requestNumber = requestNumber;
      }

      setRestrictions(newRestrictions);

      return;
    }

    const { [removedFilter.id]: removedRestriction, ...newRestrictions } = restrictions;
    const { [removedFilter.id]: removedValue, ...newFilterValues } = inputValues;

    if (removedFilter.id === 'ministryCode') {
      delete newRestrictions.departmentCode;
      delete newFilterValues.departmentCode;
    }

    if (removedFilter.id === 'ministryCode' || removedFilter.id === 'departmentCode') {
      delete newRestrictions.departmentOfficeCode;
      delete newFilterValues.departmentOfficeCode;
      setDepartmentOffices(null);
    }

    setRestrictions(newRestrictions);
  };

  const handleAddRestriction = (restriction: IRestriction, close?: () => void) => {
    if (!restriction.value && !restrictions[restriction.field]) {
      close?.();

      return;
    }

    if (isEqualWith(restrictions[restriction.field]?.value, restriction.value)) {
      close?.();

      return;
    }

    if (!restriction.value) {
      handleRemoveRestriction({
        id: restriction.field,
        type: restriction.field,
        value: restriction.value as string,
      });

      return;
    }

    const { [restriction.field]: updatedRestriction, ...restRestrictions } = restrictions;

    if (restriction.field === 'ministryCode') {
      delete restRestrictions.departmentCode;
    }

    if (restriction.field === 'ministryCode' || restriction.field === 'departmentCode') {
      delete restRestrictions.departmentOfficeCode;
    }

    setRestrictions({ ...restRestrictions, [restriction.field]: restriction });
  };

  return (
    <>
      <Table.Search>
        <div className={styles.searchWrapper}>
          <div className={styles.searchInputs}>
            <StatusSelect
              onChange={(data) => {
                handleAddRestriction({
                  field: SearchSelectField.status,
                  value: data ?? '',
                  operator: Operators.EQUALS,
                });
              }}
              defaultValue={restrictions[SearchSelectField.status]?.value as SelectOption}
              isNewLicenses={isNewLicenses}
            />
            <Input
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  handleAddRestriction({
                    field: SearchSelectField.requestNumber,
                    value: (event.target as HTMLInputElement).value,
                    operator: Operators.EQUALS,
                  });
                }
              }}
              defaultValue={(restrictions.requestNumber?.value as string) || ''}
              className={cn(styles.uppercase, styles.input)}
              name={SearchSelectField.requestNumber}
              placeholder={t('Request Number')}
            />
            <Table.FilterButton
              count={showAdvancedFilter ? undefined : filters.length}
              onClick={toggleFilterSection}
              t={t}
            />
          </div>
          <Table.ColumnSettings onChange={saveColumnSettings} data={columnSettings} t={t} />
        </div>
      </Table.Search>

      {showAdvancedFilter && (
        <Table.Filter className={styles.tableFilter}>
          <AdvancedSearch
            portalTarget={document.getElementById(PAGE_CONTENT_ID) ?? undefined}
            onRemoveFilter={handleRemoveRestriction}
            className={styles.advancedSearch}
            filters={filters}
            t={t}
            closeFiltersAfterAdd
            openFilterFromTag
          >
            <AdvancedSearch.FilterType title={t('Type of {{licenseAbbreviation}}', { licenseAbbreviation })}>
              {({ close }) => (
                <div onMouseUp={(e: MouseEvent) => e.stopPropagation()}>
                  <LicenseTypeSelect
                    onChange={(data) =>
                      data &&
                      handleAddRestriction(
                        {
                          field: SearchSelectField.licenseType,
                          value: data,
                          operator: Operators.EQUALS,
                          label: t('Type of {{licenseAbbreviation}}', { licenseAbbreviation: t(licenseAbbreviation) }),
                        },
                        close
                      )
                    }
                    defaultValue={restrictions[SearchSelectField.licenseType]?.value as SelectOption}
                    name={SearchSelectField.licenseType}
                  />
                </div>
              )}
            </AdvancedSearch.FilterType>
            <AdvancedSearch.FilterType title={columns[SearchSelectField.userReferenceNumber]!.name}>
              {({ close }) => (
                <>
                  <Input
                    defaultValue={restrictions[SearchSelectField.userReferenceNumber]?.value as string}
                    name={SearchSelectField.userReferenceNumber}
                    onChange={handleInputChange}
                    className={styles.uppercase}
                    ref={inputRef}
                  />
                  <AdvancedSearch.ApplyButton
                    onClick={() => {
                      handleAddRestriction(
                        {
                          field: SearchSelectField.userReferenceNumber,
                          value: inputValues[SearchSelectField.userReferenceNumber],
                          operator: Operators.EQUALS,
                          label: columns[SearchSelectField.userReferenceNumber]!.name,
                        },
                        close
                      );
                    }}
                    applyOnEnter
                  >
                    {t('Apply')}
                  </AdvancedSearch.ApplyButton>
                </>
              )}
            </AdvancedSearch.FilterType>
            <AdvancedSearch.FilterType title={t('Approval Ref.')}>
              {({ close }) => (
                <>
                  <Input
                    defaultValue={restrictions[SearchSelectField.approvalReference]?.value as string}
                    name={SearchSelectField.approvalReference}
                    onChange={handleInputChange}
                    className={styles.uppercase}
                    ref={inputRef}
                  />
                  <AdvancedSearch.ApplyButton
                    onClick={() => {
                      handleAddRestriction(
                        {
                          field: SearchSelectField.approvalReference,
                          value: inputValues[SearchSelectField.approvalReference],
                          operator: Operators.EQUALS,
                          label: t('Approval Ref.'),
                        },
                        close
                      );
                    }}
                    applyOnEnter
                  >
                    {t('Apply')}
                  </AdvancedSearch.ApplyButton>
                </>
              )}
            </AdvancedSearch.FilterType>
            <AdvancedSearch.FilterType title={t('Approval Date')}>
              {({ close }) => (
                <DateRangeField
                  selectedOperator={
                    (restrictions[SearchSelectField.approvalDate]?.value as DateRangeValueType)?.operator
                  }
                  onChange={(dates) => {
                    handleDateChange(dates, SearchSelectField.approvalDate, t('Approval Date'), close);
                  }}
                  startDate={(restrictions[SearchSelectField.approvalDate]?.value as DateRangeValueType)?.startDate}
                  endDate={(restrictions[SearchSelectField.approvalDate]?.value as DateRangeValueType)?.endDate}
                  date={(restrictions[SearchSelectField.approvalDate]?.value as DateRangeValueType)?.date}
                  name={SearchSelectField.approvalDate}
                  onCancel={close}
                />
              )}
            </AdvancedSearch.FilterType>
            <AdvancedSearch.FilterType title={t('Request Date')} className="p-0">
              {({ close }) => (
                <DateRangeField
                  selectedOperator={
                    (restrictions[SearchSelectField.requestDate]?.value as DateRangeValueType)?.operator
                  }
                  onChange={(dates) => {
                    handleDateChange(dates, SearchSelectField.requestDate, t('Request Date'), close);
                  }}
                  startDate={(restrictions[SearchSelectField.requestDate]?.value as DateRangeValueType)?.startDate}
                  endDate={(restrictions[SearchSelectField.requestDate]?.value as DateRangeValueType)?.endDate}
                  date={(restrictions[SearchSelectField.requestDate]?.value as DateRangeValueType)?.date}
                  name={SearchSelectField.requestDate}
                  onCancel={close}
                />
              )}
            </AdvancedSearch.FilterType>
            <AdvancedSearch.FilterType title={t('Ministry')}>
              {({ close }) => (
                <div onMouseUp={(e: MouseEvent) => e.stopPropagation()}>
                  <RimmEntitySelect
                    defaultValue={
                      restrictions[SearchSelectField.ministryCode]?.value as IMinistryRimmEntity & SelectOption
                    }
                    onChange={(data) =>
                      data &&
                      handleAddRestriction(
                        {
                          field: SearchSelectField.ministryCode,
                          value: data as SelectOption,
                          operator: Operators.EQUALS,
                          label: t('Ministry'),
                        },
                        close
                      )
                    }
                    name={SearchSelectField.ministryCode}
                    entity={RimmEntityType.ministry}
                    placeholder={t('Select...')}
                  />
                </div>
              )}
            </AdvancedSearch.FilterType>
            {restrictions[SearchSelectField.ministryCode] && (
              <AdvancedSearch.FilterType title={t('Department')}>
                {({ close }) => (
                  <div onMouseUp={(e: MouseEvent) => e.stopPropagation()}>
                    <RimmSubEntitySelect
                      defaultValue={
                        restrictions[SearchSelectField.departmentCode]?.value as IMinistryDepartmentEntity &
                          SelectOption
                      }
                      onChange={(data) => {
                        if (data) {
                          handleAddRestriction(
                            {
                              field: SearchSelectField.departmentCode,
                              value: data as SelectOption,
                              operator: Operators.EQUALS,
                              label: t('Department'),
                            },
                            close
                          );
                          setDepartmentOffices(
                            (data as IMinistryDepartmentEntity & SelectOption).ministryDepartmentOffices
                          );
                        }
                      }}
                      code={(restrictions[SearchSelectField.ministryCode]?.value as SelectOption).value}
                      entity={RimmSubEntityType.ministryDepartments}
                      name={SearchSelectField.departmentCode}
                      parentEntity={RimmEntityType.ministry}
                      placeholder={t('Select...')}
                    />
                  </div>
                )}
              </AdvancedSearch.FilterType>
            )}
            {restrictions[SearchSelectField.departmentCode] && (
              <AdvancedSearch.FilterType title={t('Department Office')}>
                {({ close }) => (
                  <div onMouseUp={(e: MouseEvent) => e.stopPropagation()}>
                    <DepartmentOfficeSelect
                      defaultValue={
                        restrictions[SearchSelectField.departmentOfficeCode]?.value as SelectOption &
                          IMinistryDepartmentOffice
                      }
                      onChange={(data) =>
                        data &&
                        handleAddRestriction(
                          {
                            field: SearchSelectField.departmentOfficeCode,
                            value: data as SelectOptionWithDescription,
                            operator: Operators.EQUALS,
                            label: t('Department Office'),
                          },
                          close
                        )
                      }
                      departmentCode={(restrictions[SearchSelectField.departmentCode]?.value as SelectOption).value}
                      ministryCode={(restrictions[SearchSelectField.ministryCode]?.value as SelectOption).value}
                      setDepartmentOffices={setDepartmentOffices}
                      departmentOffices={departmentOffices}
                    />
                  </div>
                )}
              </AdvancedSearch.FilterType>
            )}
            <AdvancedSearch.FilterType title={t('Agency Status')}>
              {({ close }) => (
                <div onMouseUp={(e: MouseEvent) => e.stopPropagation()}>
                  <OgaStatusSelect
                    onChange={(data) =>
                      data &&
                      handleAddRestriction(
                        {
                          field: SearchSelectField.ogaStatus,
                          value: data,
                          operator: Operators.EQUALS,
                          label: t('Agency Status'),
                        },
                        close
                      )
                    }
                    defaultValue={restrictions[SearchSelectField.ogaStatus]?.value as SelectOption}
                    name={SearchSelectField.ogaStatus}
                  />
                </div>
              )}
            </AdvancedSearch.FilterType>
            <AdvancedSearch.FilterType title={t('Company Code')}>
              {({ close }) => (
                <div onMouseUp={(e: MouseEvent) => e.stopPropagation()}>
                  <RimmEntitySelect
                    defaultValue={
                      restrictions[SearchSelectField.companyCode]?.value as ICompanyRimmEntity & SelectOption
                    }
                    onChange={(data) =>
                      data &&
                      handleAddRestriction(
                        {
                          field: SearchSelectField.companyCode,
                          value: data as SelectOption,
                          operator: Operators.EQUALS,
                          label: t('Company Code'),
                        },
                        close
                      )
                    }
                    name={SearchSelectField.companyCode}
                    entity={RimmEntityType.company}
                    minSuggest={2}
                    keepInputValueAfterSelect
                  />
                </div>
              )}
            </AdvancedSearch.FilterType>
            <AdvancedSearch.FilterType title={t('Commodity Code')}>
              {({ close }) => (
                <div onMouseUp={(e: MouseEvent) => e.stopPropagation()}>
                  <RimmEntitySelect
                    defaultValue={
                      restrictions[SearchSelectField.commodityCode]?.value as ICommodityRimmEntity & SelectOption
                    }
                    onChange={(data) =>
                      data &&
                      handleAddRestriction(
                        {
                          field: SearchSelectField.commodityCode,
                          value: data as SelectOption,
                          operator: Operators.EQUALS,
                          label: t('Commodity Code'),
                        },
                        close
                      )
                    }
                    name={SearchSelectField.commodityCode}
                    entity={RimmEntityType.commodity}
                    minSuggest={2}
                    keepInputValueAfterSelect
                  />
                </div>
              )}
            </AdvancedSearch.FilterType>
            <AdvancedSearch.FilterType title={t('Last Updated On')}>
              {({ close }) => (
                <DateRangeField
                  selectedOperator={
                    (restrictions[SearchSelectField.lastOperationDate]?.value as DateRangeValueType)?.operator
                  }
                  startDate={
                    (restrictions[SearchSelectField.lastOperationDate]?.value as DateRangeValueType)?.startDate
                  }
                  onChange={(dates) =>
                    handleDateChange(dates, SearchSelectField.lastOperationDate, t('Last Updated On'), close)
                  }
                  endDate={(restrictions[SearchSelectField.lastOperationDate]?.value as DateRangeValueType)?.endDate}
                  date={(restrictions[SearchSelectField.lastOperationDate]?.value as DateRangeValueType)?.date}
                  name={SearchSelectField.lastOperationDate}
                  onCancel={close}
                />
              )}
            </AdvancedSearch.FilterType>
          </AdvancedSearch>
        </Table.Filter>
      )}
    </>
  );
}

export default LicensesSearch;
